/**
 * DecreeLinesSection.tsx — Dekretacja faktury: READ-ONLY widok księgowań Wn/Ma.
 *
 * Mirror `DocumentItemsSection` (patrz nagłówek tamtego pliku) — czysty widok danych
 * z GET, bez własnego fetch/mutate. Ten sam model grupowania/bilansu co
 * `DecreeLinesEditor` (`lib/documents/decree-lines.ts`), prezentacja jako dwie
 * wirtualne kolumny Wn/Ma (kwota w komórce wg `side`, druga komórka pusta).
 *
 * Wpięcie na stronę faktury (GET response → `lines: DecreeLine[]`) jest POZA
 * zakresem tego increment.
 */

import { useTranslation } from 'react-i18next';

import { TableEmpty } from '@/components/configurable-table/TableEmpty';
import {
  ACCOUNT_SEGMENT_FIELDS,
  accountSegmentLabel,
  behindPostingDescription,
  behindPostingLabel,
  computeGroupBalance,
  computeOverallBalance,
  type DecreeLine,
  groupDecreeLines,
  normalizeBehindPosting,
} from '@/lib/documents/decree-lines';
import { monthStartOf } from '@/lib/documents/period-date';
import { formatMonthLabel } from '@/lib/month-label';

export interface DecreeLinesSectionProps {
  lines: DecreeLine[] | null | undefined;
  /**
   * Typ dokumentu w Symfonii — nadpisanie z dokumentu (`symfoniaTypeCode`) albo wartość
   * domyślna z mapowania w integracji (`defaultCode`). Podane → jedna linia nad tabelą,
   * żeby w odczycie było widać, z jakim typem dokument pójdzie do księgowości.
   */
  symfoniaTypeCode?: string | null;
  symfoniaTypeDefaultCode?: string | null;
  /** Rok obrachunkowy przypisany do dokumentu (`symfoniaFiscalYear` z GET faktury). */
  fiscalYear?: string | null;
  /** Data okresu (miesiąc księgowy) zapisana na dokumencie — `YYYY-MM-01` lub `null`. */
  periodDate?: string | null;
  /**
   * Firma ma aktywną integrację FK (jest z czego wybierać typ dokumentu Symfonia) — ta sama
   * bramka co pole Daty okresu w edytorze i przycisk wysyłki. Bez niej linia okresu się nie
   * pokazuje (pojęcie okresu nie ma sensu bez integracji).
   */
  symfoniaIntegrationActive?: boolean;
  /**
   * Data dokumentu (`YYYY-MM-DD`) — gdy `periodDate` jest puste, a pokazujemy typ Symfonia,
   * wyświetlamy jej miesiąc z dopiskiem „(z daty dokumentu)" (tak samo wyliczy go wysyłka).
   */
  documentDate?: string | null;
  /** Akcje nad tabelą (np. „Wyślij do Symfonii") — renderowane w tej samej linii co opisy. */
  actions?: React.ReactNode;
}

const EMPTY_DASH = '—';
const th = 'px-3 py-2 text-left text-xs font-semibold text-text-secondary';
const thRight = 'px-3 py-2 text-right text-xs font-semibold text-text-secondary';
const td = 'px-3 py-2 text-text-primary';
const tdRight = 'px-3 py-2 text-right tabular-nums text-text-primary';

function formatMoney(value: string | null | undefined): string {
  if (value == null || value === '') return EMPTY_DASH;
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatMoneyNumber(value: number): string {
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function DecreeLinesSection({
  lines,
  symfoniaTypeCode,
  symfoniaTypeDefaultCode,
  fiscalYear,
  periodDate,
  symfoniaIntegrationActive = false,
  documentDate,
  actions,
}: DecreeLinesSectionProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'pl';
  const rows = lines ?? [];
  const groups = groupDecreeLines(rows);
  const overallBalance = computeOverallBalance(rows);

  // Typ efektywny: nadpisanie z dokumentu, w jego braku wartość z mapowania integracji.
  const effectiveSymfoniaType = symfoniaTypeCode || symfoniaTypeDefaultCode || null;
  // Data okresu: zapisana, a w jej braku miesiąc daty dokumentu (z dopiskiem). Pokazujemy ją
  // tylko przy aktywnej integracji FK (flaga ze strony — ten sam sygnał co pole w edytorze
  // i przycisk wysyłki), bo bez niej pojęcie okresu nie ma sensu.
  const savedPeriodLabel = formatMonthLabel(periodDate, locale);
  // Podpowiedź z daty dokumentu przechodzi przez `monthStartOf` (jak w drafcie na stronie):
  // literówka w roczniku nie daje etykiety, której i tak nie dałoby się zapisać.
  const fallbackPeriodLabel = savedPeriodLabel
    ? null
    : formatMonthLabel(monthStartOf(documentDate), locale);
  const periodLabel = symfoniaIntegrationActive ? (savedPeriodLabel ?? fallbackPeriodLabel) : null;
  const hasHeader = !!effectiveSymfoniaType || !!fiscalYear || !!periodLabel || !!actions;
  const symfoniaTypeLine = hasHeader ? (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1"
      data-testid="decree-lines-section-header"
    >
      {fiscalYear && (
        <p className="text-xs text-text-secondary" data-testid="decree-lines-section-fiscal-year">
          {t('tenant.documents.decree.editor.fiscalYearLabel')}:{' '}
          <span className="font-medium text-text-primary">{fiscalYear}</span>
        </p>
      )}
      {effectiveSymfoniaType && (
        <p className="text-xs text-text-secondary" data-testid="decree-lines-section-symfonia-type">
          {t('tenant.documents.decree.editor.symfoniaTypeLabel')}:{' '}
          <span className="font-medium text-text-primary">{effectiveSymfoniaType}</span>
        </p>
      )}
      {periodLabel && (
        <p className="text-xs text-text-secondary" data-testid="decree-lines-section-period-date">
          {t('tenant.documents.decree.editor.periodDateLabel')}:{' '}
          <span className="font-medium text-text-primary">{periodLabel}</span>
          {!savedPeriodLabel && ' '}
          {!savedPeriodLabel && (
            <span className="text-text-tertiary">
              {t('tenant.documents.decree.editor.periodDateFromDocument')}
            </span>
          )}
        </p>
      )}
      {actions && <div className="ml-auto">{actions}</div>}
    </div>
  ) : null;

  if (rows.length === 0) {
    return (
      <div className="space-y-2">
        {symfoniaTypeLine}
        <div className="overflow-hidden rounded border border-border-primary">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-primary bg-bg-secondary">
                  {ACCOUNT_SEGMENT_FIELDS.map((field) => (
                    <th key={field} className={th}>
                      {accountSegmentLabel(field, t)}
                    </th>
                  ))}
                  <th className={th}>{t('tenant.documents.decree.columns.description')}</th>
                  <th className={thRight}>{t('tenant.documents.decree.columns.debit')}</th>
                  <th className={thRight}>{t('tenant.documents.decree.columns.credit')}</th>
                  <th className={th}>{t('tenant.documents.decree.columns.transaction')}</th>
                  <th className={th}>{t('tenant.documents.decree.columns.behindPosting')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    colSpan={ACCOUNT_SEGMENT_FIELDS.length + 5}
                    data-testid="decree-lines-section-empty"
                  >
                    <TableEmpty
                      title={t('tenant.documents.decree.emptyTitle')}
                      description={t('tenant.documents.decree.empty')}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="decree-lines-section">
      {symfoniaTypeLine}
      {groups.map(({ groupNo, lines: groupLines }) => {
        const groupBalance = computeGroupBalance(groupLines);
        return (
          <div
            key={groupNo}
            className="overflow-hidden rounded border border-border-primary"
            data-testid={`decree-lines-section-group-${groupNo}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-primary bg-bg-secondary px-3 py-2">
              <span className="text-sm font-semibold text-text-primary">
                {t('tenant.documents.decree.editor.groupLabel', { number: groupNo })}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  groupBalance.balanced
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                }`}
                data-testid={`decree-lines-section-group-${groupNo}-balance-badge`}
              >
                {groupBalance.balanced
                  ? t('tenant.documents.decree.balance.balanced')
                  : t('tenant.documents.decree.balance.diff', {
                      amount: formatMoneyNumber(Math.abs(groupBalance.diff)),
                    })}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-primary bg-bg-secondary">
                    {ACCOUNT_SEGMENT_FIELDS.map((field) => (
                      <th key={field} className={th}>
                        {accountSegmentLabel(field, t)}
                      </th>
                    ))}
                    <th className={th}>{t('tenant.documents.decree.columns.description')}</th>
                    <th className={thRight}>{t('tenant.documents.decree.columns.debit')}</th>
                    <th className={thRight}>{t('tenant.documents.decree.columns.credit')}</th>
                    <th className={th}>{t('tenant.documents.decree.columns.transaction')}</th>
                    <th className={th}>{t('tenant.documents.decree.columns.behindPosting')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groupLines.map((line) => (
                    <tr
                      key={line.id}
                      className="border-b border-border-primary last:border-0"
                      data-testid={`decree-lines-section-row-${line.id}`}
                    >
                      {ACCOUNT_SEGMENT_FIELDS.map((field) => (
                        <td key={field} className={td}>
                          {line[field] || EMPTY_DASH}
                        </td>
                      ))}
                      <td className={td}>{line.description || EMPTY_DASH}</td>
                      <td className={tdRight}>
                        {line.side === 'debit' ? formatMoney(line.amount) : EMPTY_DASH}
                      </td>
                      <td className={tdRight}>
                        {line.side === 'credit' ? formatMoney(line.amount) : EMPTY_DASH}
                      </td>
                      <td className={td}>
                        {line.isTransaction ? t('common.yes') : t('common.no')}
                      </td>
                      <td className={`${td} tabular-nums`}>
                        {/* Wartość liczbowa (jak w bazie i w Symfonii); opis w `title`. */}
                        <span
                          title={behindPostingDescription(
                            normalizeBehindPosting(line.behindPosting),
                            t,
                          )}
                        >
                          {behindPostingLabel(normalizeBehindPosting(line.behindPosting))}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div
        className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 rounded border border-border-primary bg-bg-secondary px-4 py-3 text-sm"
        data-testid="decree-lines-section-totals"
      >
        <span className="text-text-secondary">
          {t('tenant.documents.decree.balance.totalDebit')}:{' '}
          <span className="font-semibold tabular-nums text-text-primary">
            {formatMoneyNumber(overallBalance.debit)}
          </span>
        </span>
        <span className="text-text-secondary">
          {t('tenant.documents.decree.balance.totalCredit')}:{' '}
          <span className="font-semibold tabular-nums text-text-primary">
            {formatMoneyNumber(overallBalance.credit)}
          </span>
        </span>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
            overallBalance.balanced
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          }`}
          data-testid="decree-lines-section-balance-badge"
        >
          {overallBalance.balanced
            ? t('tenant.documents.decree.balance.balanced')
            : t('tenant.documents.decree.balance.notBalanced')}
        </span>
      </div>
    </div>
  );
}
